/**
 * ตรรกะล้วนของหน้าจอตารางสิทธิ์ — แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 *
 * แนวเดียวกับ `desktop/goodsReceipts/goodsReceiptView.ts` — สิ่งที่อยู่ที่นี่คือ
 * คำถามที่ตอบได้โดยไม่ต้องเรนเดอร์อะไรเลย
 */

import type {
  ModulePermissionRow,
  PermissionFlagKey,
  PermissionFlags,
  SetModulePermissionBody,
} from '../../lib/rbacAdmin/types';

export interface PermissionFlagColumn {
  readonly key: PermissionFlagKey;
  readonly label: string;
  /** ชื่อ action ใน RBAC matrix (D-008) — ใช้ประกอบ label ที่ screen reader อ่าน */
  readonly action: 'CREATE' | 'VIEW' | 'EDIT' | 'APPROVE';
}

/**
 * สี่คอลัมน์ของ matrix ตามลำดับที่ contract เขียน flag ไว้
 *
 * ทั้งสี่เป็น **checkbox อิสระต่อกัน** ไม่ใช่ระดับสิทธิ์ที่ไล่จากน้อยไปมาก: แผนก
 * หนึ่งถือ `can_edit` และ `can_approve` บน module เดียวกันได้ (segregation of duties
 * เป็นกฎ **รายเอกสาร** approver ≠ creator ที่ service บังคับ ไม่ใช่ข้อห้ามที่ matrix)
 * การผูกให้ติ๊กตัวหนึ่งแล้วลากอีกตัวตามไปด้วยจึงเป็นกติกาที่หน้าจอคิดขึ้นเอง และจะ
 * ทำให้ผู้ดูแลมอบสิทธิ์ที่เขาไม่ได้ตั้งใจมอบ
 */
export const PERMISSION_FLAG_COLUMNS: readonly PermissionFlagColumn[] = [
  { key: 'can_create', label: 'สร้าง', action: 'CREATE' },
  { key: 'can_view', label: 'ดู', action: 'VIEW' },
  { key: 'can_edit', label: 'แก้ไข', action: 'EDIT' },
  { key: 'can_approve', label: 'อนุมัติ', action: 'APPROVE' },
];

/**
 * แถวนี้มอบสิทธิ์ผ่าน matrix ได้หรือไม่ — **อ่านจากธงที่เซิร์ฟเวอร์ส่งมาเท่านั้น**
 *
 * D-060: module กลุ่ม System Administration เข้าถึงได้ทาง system role `ADMIN`
 * เท่านั้น ห้ามมาจาก `department_module_permission` เลย หน้าจอจึงไม่ hardcode
 * `RBAC_ADMIN`/`SYSTEM_CONFIG`/`DEVICE_REGISTRY` ไว้เอง — รายการที่ frontend จำเอง
 * จะ drift ออกจาก `SYSTEM_ADMIN_MODULE_CODES` ฝั่ง backend และ drift คือศัตรูตัวจริง
 * ของกฎข้อนี้ (เหตุผลเดียวกับที่ D-060 ไม่ใส่ CHECK constraint ในฐานข้อมูล)
 */
export function isRowGrantable(row: ModulePermissionRow): boolean {
  return !row.is_system_admin_module;
}

/**
 * แถวที่หน้าจอยอมประกอบ `POST` ให้ — **ด่านเดียวที่ทางเขียนเดินผ่าน**
 *
 * ทุกเส้นทางบันทึกในหน้าจอนี้เรียกฟังก์ชันนี้ก่อนเสมอ ไม่ใช่พึ่ง `disabled` ของ
 * ช่องติ๊กอย่างเดียว: `disabled` เป็นเรื่องของ DOM ที่เปลี่ยนได้จาก devtools ส่วน
 * นี่คือเงื่อนไขในโค้ดที่ทำให้ **ไม่มีเส้นทางใดในหน้าจอประกอบคำขอของ module กลุ่ม
 * System Administration ขึ้นมาได้เลย** — และนั่นคือสิ่งที่ต้องจริงเพื่อให้หน้าจอกับ
 * `422 SYSTEM_ADMIN_MODULE_NOT_GRANTABLE` ของ API พูดตรงกัน
 */
export function grantableRows(
  modules: readonly ModulePermissionRow[],
): readonly ModulePermissionRow[] {
  return modules.filter(isRowGrantable);
}

/** ถอดเฉพาะสี่ flag ออกจากแถว — ทิ้ง `module_name`/ธงที่ `POST` ไม่รับ */
export function flagsOf(row: PermissionFlags): PermissionFlags {
  return {
    can_create: row.can_create,
    can_view: row.can_view,
    can_edit: row.can_edit,
    can_approve: row.can_approve,
  };
}

/** สี่ flag เท่ากันทั้งชุดหรือไม่ — ใช้ตัดสินว่าแถวนี้ "มีอะไรให้บันทึก" */
export function flagsEqual(left: PermissionFlags, right: PermissionFlags): boolean {
  return PERMISSION_FLAG_COLUMNS.every((column) => left[column.key] === right[column.key]);
}

/** สลับ flag เดียวโดยไม่แตะอีกสามตัว */
export function toggleFlag(flags: PermissionFlags, key: PermissionFlagKey): PermissionFlags {
  return { ...flags, [key]: !flags[key] };
}

/**
 * ประกอบ body ของ `POST /v1/admin/permissions` — คืน `null` เมื่อแถวนั้นมอบไม่ได้
 *
 * การคืน `null` แทนการ throw ทำให้ผู้เรียกต้องจัดการกรณีนั้นอย่างชัดเจน และทำให้
 * เทสต์พิสูจน์ได้ว่า **ไม่มีทางประกอบคำขอของ module กลุ่ม System Administration**
 * โดยไม่ต้องเรนเดอร์หน้าจอเลย
 */
export function toSaveBody(
  departmentId: string,
  row: ModulePermissionRow,
  flags: PermissionFlags,
): SetModulePermissionBody | null {
  if (!isRowGrantable(row)) {
    return null;
  }
  return {
    department_id: departmentId,
    module_code: row.module_code,
    ...flagsOf(flags),
  };
}

/** ค่าเริ่มต้นของ draft ทั้ง grid — ค่าที่เซิร์ฟเวอร์ส่งมา ไม่ใช่ค่าที่หน้าจอเดา */
export function draftFromMatrix(
  modules: readonly ModulePermissionRow[],
): Readonly<Record<string, PermissionFlags>> {
  const draft: Record<string, PermissionFlags> = {};
  for (const row of modules) {
    draft[row.module_code] = flagsOf(row);
  }
  return draft;
}
